import React, { ReactNode } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import clsx from 'clsx';
import './XSButton.css';
import { HtmlElementDefaults } from '../../type/HtmlElementDefaults';
import { XSLoadingSpinner } from '../loading/spinner/XSLoadingSpinner';
import { XSIcon } from '../icon/XSIcon';
import { Styles } from '../../type/Styles';
import { XSText } from '../text/XSText';
import { XSCard } from '../card/XSCard';

export type XSButtonInput = HtmlElementDefaults & {
    type?: 'cell';
    color?: 'default' | Styles.Color;
    fill?: 'none' | 'solid' | 'outline';
    size?: 'xs' | 'sm' | 'default' | 'lg';
    shape?: 'square';
    disabled?: boolean;
    loading?: boolean;
    block?: boolean;
    icon?: ReactNode;
    label?: ReactNode;
    direction?: 'row' | 'column';
    onClick?(event?: React.MouseEvent<HTMLElement>): void;
    to?: To;
};

type To = string | [string, Object];

export const XSButton = React.forwardRef((input: XSButtonInput, ref: any) => {
    let {
        type,
        color = 'default',
        fill,
        size,
        shape,
        disabled,
        loading = false,
        block,
        style,
        icon,
        label,
        direction,
        onClick,
        to,
    } = input;

    if (type === 'cell') {
        fill = fill || 'none';
        shape = shape || 'square';
    }

    let isIconOnly = false;

    if (!input.children && icon) {
        isIconOnly = true;
    } else if (
        React.Children.count(input.children) === 1 &&
        (React.Children.toArray(input.children)[0] as any)?.type === XSIcon
    ) {
        isIconOnly = true;
    }

    const className = clsx(
        input.className,
        `XSButton fill-${fill || 'solid'}-${color || 'default'} size-${size || 'default'} shape-${
            shape || ''
        } direction-${direction || ''} type-${type || ''}`,
        { loading, block, iconOnly: isIconOnly },
    );

    const content = (
        <>
            {label && <XSCard className={'XSButton-label'}>{label}</XSCard>}

            {loading && (
                <div className={'XSButton-loading-overlay'}>
                    <XSLoadingSpinner />
                </div>
            )}

            {icon ? typeof icon === 'string' ? <XSIcon>{icon}</XSIcon> : icon : null}

            {input.children}
        </>
    );

    if (to) {
        return (
            <Link to={to} className={className} style={style} onClick={onClick}>
                {content}
            </Link>
        );
    }

    return (
        <button ref={ref} className={className} disabled={disabled || loading} style={style} onClick={onClick}>
            {content}
        </button>
    );
});

function Link({ to, style, className, onClick, children }: HtmlElementDefaults & { to: To }) {
    const params = useParams();

    let path = '';

    const handleObject = (obj: any) => Object.keys(obj).forEach((key) => (path = path.replace(`:${key}`, obj[key])));

    if (typeof to === 'string') {
        path = to;
        handleObject(params);
    } else {
        path = to[0];
        handleObject(params);
        handleObject(to[1] || {});
    }

    if (path.startsWith('http')) {
        return (
            <a href={path} target={'_blank'} className={clsx(className)} style={style} onClick={onClick}>
                {children}
            </a>
        );
    }

    return (
        <NavLink to={path} className={clsx(className)} style={style} onClick={onClick}>
            {children}
        </NavLink>
    );
}
